import { MIPContext } from '@dealstack/frontend/app/pages/Project/MIP/MIP';
import { Divider } from '@dealstack/frontend/shared/components/fundament/Divider/Divider';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { HelpButton } from '@dealstack/frontend/shared/components/HelpButton/HelpButton';
import { PrimarySideList } from '@dealstack/frontend/shared/components/listOld/PrimarySideList/PrimarySideList';
import { SideTabItem } from '@dealstack/frontend/shared/components/listOld/SideTabItem/SideTabItem';
import {
	TEST_ELEMENT_IDS,
	UIElementIdentifier,
} from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import { useTranslationMipCreate } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useLocalStorageModalController } from '@dealstack/frontend/shared/other/react/hooks/useLocalStorageModalController';
import { useContext } from 'react';
import styled from 'styled-components';

import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';
import { StorageKeys } from '@dealstack/frontend/shared/helper/storageUtils';
import { Button } from '@mantine/core';
import { ISideListConfigEntry } from '@dealstack/frontend/shared/components/listOld/PrimarySideList/PrimarySideList.types';
import { MIPCreateContext } from './MIPCreateContext';
import { MipCreateHelpModal } from './MipCreateHelpModal/MipCreateHelpModal';

interface Props {
	className?: string;
	onDeleteClick: () => void;
}

const MIPCreateSidebar = ({ className, onDeleteClick }: Props) => {
	const tMipCreate = useTranslationMipCreate();
	const mipController = useContext(MIPContext);
	const dealController = useContext(DealContext);
	const { deal } = dealController;

	const { showModal, markModalAsSeen, setShowModal } = useLocalStorageModalController(
		StorageKeys.DID_SAW_MIP_CREATE_HELP_ON_MIP,
		mipController?.mipId
	);

	const mipCreateController = useContext(MIPCreateContext);
	if (!mipCreateController) {
		return null;
	}

	return (
		<div className={className}>
			<SidebarTop>
				<DealNameText variant="h5" bold>
					{tMipCreate('sidebar.new_deal')}
				</DealNameText>
				<CompanyNameText variant="p-md" colorFaded>
					{deal?.targetName}
				</CompanyNameText>
			</SidebarTop>
			<Divider />
			<SidebarBottom>
				<StepsText variant="button"> {tMipCreate('sidebar.steps')}</StepsText>
				<PrimarySideList
					onActiveChange={mipCreateController.onSidebarLinkClick}
					config={mipCreateController.sideBarConfig as ISideListConfigEntry[]}
					activeKey={mipCreateController.activeSidebarKey}
				/>
			</SidebarBottom>

			<SidebarFarBottom>
				<Button onClick={onDeleteClick} variant="subtle" color="red" mb="0.5rem">
					{tMipCreate('draft.delete.button', { defaultValue: 'Delete draft' })}
				</Button>

				<UIElementIdentifier identifier={TEST_ELEMENT_IDS.MIP_CREATE_HELP_BUTTON}>
					<HelpButton onClick={() => setShowModal(true)} />
				</UIElementIdentifier>
			</SidebarFarBottom>

			{showModal ? <MipCreateHelpModal onClose={markModalAsSeen} /> : null}
		</div>
	);
};

const SidebarTop = styled.div``;
const SidebarBottom = styled.div``;
const SidebarFarBottom = styled.div``;
const DealNameText = styled(Text)``;
const CompanyNameText = styled(Text)``;
const StepsText = styled(Text)``;

const Styled = styled(MIPCreateSidebar)`
	display: flex;
	flex-direction: column;
	min-width: inherit;
	width: inherit;

	${StepsText} {
		margin-bottom: 1.5rem;
	}

	${SidebarTop} {
		display: flex;
		flex-direction: column;
	}

	${SideTabItem} {
		--background-color-inactive: ${({ theme }) => theme.v2.color.neutral[200]};
	}

	${SidebarBottom}, ${SidebarTop}, ${SidebarFarBottom} {
		padding: 1.5rem 1rem 1.5rem 2rem;
	}

	${SidebarFarBottom} {
		margin-top: auto;
		justify-self: end;
	}
`;

export { Styled as MIPCreateSidebar };
